'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Activity, Layers, Sparkles, Server } from 'lucide-react';

export default function Header() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const pathname = usePathname();

    const tools = [
        { name: "Spark Analyzer", link: "/spark-analyzer", title: "Minecraft Server Optimization Tool", icon: Activity },
        { name: "Server Status", link: "/server-status", title: "Minecraft Server Status & Ping Checker", icon: Server },
        { name: "Tab Generator", link: "/tab-generator", title: "Minecraft Tab list Generator", icon: Layers },
        { name: "MOTD Generator", link: "/motd-generator", title: "Minecraft Server MOTD Generator", icon: Sparkles }
    ];

    useEffect(() => {
        setIsMenuOpen(false);
        setIsDropdownOpen(false);
    }, [pathname]);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isMenuOpen]);

    return (
        <header
            className={`sticky top-0 z-50 w-full transition-all duration-500 ${scrolled
                ? 'bg-white/80 backdrop-blur-xl border-b border-slate-100 shadow-[0_1px_30px_-4px_rgba(0,168,89,0.08)]'
                : 'bg-white/60 backdrop-blur-md border-b border-transparent'
                }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-[4.5rem]">

                    {/* Logo */}
                    <Link href="/" className="flex items-center gap-2.5 group">

                        <span className="text-lg font-extrabold tracking-tight text-slate-800 group-hover:text-slate-900 transition-colors">
                            BanglaVerse<span className="text-green-800"> Cloud</span>
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-2">
                        <NavLink href="https://billing.banglaverse.net/" active={false}>Billing</NavLink>
                        <NavLink href="https://panel.banglaverse.net" active={false}>Panel</NavLink>

                        {/* Tools Dropdown */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${isDropdownOpen
                                    ? 'bg-green-50 text-green-800 shadow-sm ring-1 ring-green-200'
                                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                    }`}
                            >
                                Tools
                                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown Panel */}
                            <div className={`absolute right-0 mt-2 w-56 transition-all duration-300 origin-top-right ${isDropdownOpen
                                ? 'opacity-100 scale-100 translate-y-0'
                                : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                                }`}>
                                <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 py-2 overflow-hidden">
                                    {tools.map((tool) => {
                                        const Icon = tool.icon;
                                        return (
                                            <Link
                                                key={tool.link}
                                                href={tool.link}
                                                title={tool.title}
                                                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-600 hover:bg-green-50 hover:text-green-800 transition-all group"
                                            >
                                                <div className="w-7 h-7 rounded-lg bg-green-50 border border-green-200/60 text-green-700 flex items-center justify-center shrink-0 group-hover:bg-green-600 group-hover:text-white transition-colors">
                                                    <Icon className="w-3.5 h-3.5" />
                                                </div>
                                                <div>
                                                    <span className="block font-semibold">{tool.name}</span>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* CTA */}
                        <Link
                            href="https://discord.gg/tnm9jP84Mv"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 px-5 py-2 rounded-xl text-sm font-bold bg-gradient-to-r from-green-600 to-emerald-500 text-white shadow-md shadow-green-500/20 hover:shadow-lg hover:shadow-green-500/30 hover:-translate-y-0.5 active:scale-95 transition-all"
                        >
                            Join Discord
                        </Link>
                    </nav>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="md:hidden relative w-10 h-10 rounded-xl text-slate-600 hover:text-slate-700 hover:bg-slate-50 flex items-center justify-center transition-all active:scale-90"
                        aria-label="Toggle menu"
                    >
                        <div className="w-5 h-4 flex flex-col justify-between">
                            <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 origin-left ${isMenuOpen ? 'rotate-45 w-[22px]' : 'w-5'}`} />
                            <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 ${isMenuOpen ? 'opacity-0 translate-x-2' : 'w-4 opacity-100'}`} />
                            <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 origin-left ${isMenuOpen ? '-rotate-45 w-[22px]' : 'w-5'}`} />
                        </div>
                    </button>
                </div>
            </div>

            {/* Mobile Navigation Menu */}
            <div className={`md:hidden fixed inset-x-0 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${isMenuOpen ? 'top-[4.5rem] opacity-100' : 'top-[4.5rem] opacity-0 pointer-events-none'
                }`} style={{ height: isMenuOpen ? 'calc(100vh - 4.5rem)' : '0' }}>
                <div className="bg-white/95 backdrop-blur-xl h-full overflow-y-auto border-t border-slate-100">
                    <div className="px-4 pt-6 pb-8 space-y-2">
                        <MobileNavLink href="https://billing.banglaverse.net/">Billing</MobileNavLink>
                        <MobileNavLink href="https://panel.banglaverse.net">Panel</MobileNavLink>

                        <div className="pt-4 mt-3 border-t border-slate-100">
                            <p className="px-4 mb-3 text-[11px] font-bold text-slate-400 uppercase tracking-[0.15em]">Developer Tools</p>
                            {tools.map((tool) => {
                                const Icon = tool.icon;
                                return (
                                    <MobileNavLink key={tool.link} href={tool.link} title={tool.title}>
                                        <div className="w-6 h-6 rounded-md bg-green-50 text-green-700 flex items-center justify-center mr-2.5 shrink-0 inline-flex">
                                            <Icon className="w-3.5 h-3.5" />
                                        </div>
                                        <span>{tool.name}</span>
                                    </MobileNavLink>
                                );
                            })}
                        </div>

                        <div className="pt-6">
                            <Link
                                href="https://discord.gg/tnm9jP84Mv"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl text-sm font-bold bg-gradient-to-r from-green-600 to-emerald-500 text-white shadow-lg shadow-green-500/20 active:scale-95 transition-all"
                            >
                                Join Discord
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}

function NavLink({ href, children, active }: { href: string; children: React.ReactNode; active?: boolean; title?: string }) {
    return (
        <Link
            href={href}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${active
                ? 'bg-green-50 text-green-800 ring-1 ring-green-200'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
        >
            {children}
        </Link>
    );
}

function MobileNavLink({ href, children, title }: { href: string; children: React.ReactNode; title?: string }) {
    return (
        <Link
            href={href}
            title={title}
            className="flex items-center px-4 py-3.5 rounded-2xl text-[15px] font-semibold text-slate-700 hover:bg-green-50 hover:text-green-800 transition-all active:scale-[0.98]"
        >
            {children}
        </Link>
    );
}

/* Icons */
const ChevronDownIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
);
